import { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';
import { enqueue, flushQueue } from '../services/offlineQueue';
import { isoDate, uuid } from '../utils';

const EMPTY = { goals: [], habits: [], moods: [], chats: [], insights: [], prefs: null };

function isNetworkError(err) {
  if (!err) return false;
  if (err.name === 'TypeError') return true;
  const msg = String(err.message || '');
  return /Failed to fetch|NetworkError|Load failed/i.test(msg);
}

async function executeQueued(item) {
  if (item.method === 'PUT') return api.putData(item.body);
  if (item.method === 'DELETE') return api.deleteData(item.body.sk);
  throw new Error('Unknown queued op');
}

export function useElizaData() {
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const fresh = await api.getData();
      setData({ ...EMPTY, ...fresh });
      setError(null);
    } catch (err) {
      setError(err);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const fresh = await api.getData();
        if (alive) setData({ ...EMPTY, ...fresh });
      } catch (err) {
        if (alive) setError(err);
      } finally {
        if (alive) setLoading(false);
      }
      try {
        await flushQueue(executeQueued);
        await refresh();
      } catch (err) {
        console.warn('initial flush failed', err);
      }
    })();
    return () => {
      alive = false;
    };
  }, [refresh]);

  useEffect(() => {
    function handleOnline() {
      setOnline(true);
      flushQueue(executeQueued).then(refresh).catch(() => {});
    }
    function handleOffline() {
      setOnline(false);
    }
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [refresh]);

  const putItem = useCallback(async (item) => {
    try {
      return await api.putData(item);
    } catch (err) {
      if (isNetworkError(err)) {
        await enqueue({ method: 'PUT', body: item });
        return null;
      }
      throw err;
    }
  }, []);

  const deleteItemRemote = useCallback(async (sk) => {
    try {
      return await api.deleteData(sk);
    } catch (err) {
      if (isNetworkError(err)) {
        await enqueue({ method: 'DELETE', body: { sk } });
        return null;
      }
      throw err;
    }
  }, []);

  const saveGoal = useCallback(
    async (input) => {
      const id = input.id || uuid();
      const goal = {
        id,
        title: input.title?.trim() || '',
        milestones: input.milestones || [],
        done: input.done ?? false,
        dueDate: input.dueDate || null,
        createdAt: input.createdAt || new Date().toISOString()
      };
      const item = { ...goal, sk: `goal#${id}` };
      setData((prev) => {
        const exists = prev.goals.some((g) => g.id === id);
        const goals = exists ? prev.goals.map((g) => (g.id === id ? goal : g)) : [...prev.goals, goal];
        return { ...prev, goals };
      });
      await putItem(item);
      return goal;
    },
    [putItem]
  );

  const deleteGoal = useCallback(
    async (id) => {
      setData((prev) => ({ ...prev, goals: prev.goals.filter((g) => g.id !== id) }));
      await deleteItemRemote(`goal#${id}`);
    },
    [deleteItemRemote]
  );

  const saveHabit = useCallback(
    async (input) => {
      const id = input.id || uuid();
      const habit = {
        id,
        title: input.title?.trim() || '',
        type: input.type || 'positive',
        frequency: input.frequency || { type: 'daily' },
        weeklyTarget: input.weeklyTarget,
        streak: input.streak ?? 0,
        paused: input.paused ?? false,
        checkIns: input.checkIns || [],
        createdAt: input.createdAt || new Date().toISOString()
      };
      const item = { ...habit, sk: `habit#${id}` };
      setData((prev) => {
        const exists = prev.habits.some((h) => h.id === id);
        const habits = exists ? prev.habits.map((h) => (h.id === id ? habit : h)) : [...prev.habits, habit];
        return { ...prev, habits };
      });
      await putItem(item);
      return habit;
    },
    [putItem]
  );

  const deleteHabit = useCallback(
    async (id) => {
      setData((prev) => ({ ...prev, habits: prev.habits.filter((h) => h.id !== id) }));
      await deleteItemRemote(`habit#${id}`);
    },
    [deleteItemRemote]
  );

  const checkInHabit = useCallback(
    async (habit, note) => {
      const today = isoDate();
      const already = (habit.checkIns || []).some((c) => (c.date || '').slice(0, 10) === today);
      const checkIns = already
        ? (habit.checkIns || []).filter((c) => (c.date || '').slice(0, 10) !== today)
        : [...(habit.checkIns || []), { date: today, ...(note ? { note } : {}) }];
      return saveHabit({ ...habit, checkIns });
    },
    [saveHabit]
  );

  const toggleHabitPause = useCallback(
    async (habit) => saveHabit({ ...habit, paused: !habit.paused }),
    [saveHabit]
  );

  const saveMood = useCallback(
    async (score, note = '') => {
      const id = uuid();
      const mood = { id, score, note: note.trim(), date: new Date().toISOString() };
      const item = { ...mood, sk: `mood#${mood.date}` };
      setData((prev) => ({ ...prev, moods: [mood, ...prev.moods] }));
      await putItem(item);
      return mood;
    },
    [putItem]
  );

  const savePrefs = useCallback(
    async (patch) => {
      const next = { ...(data.prefs || {}), ...patch, sk: 'prefs#main' };
      setData((prev) => ({ ...prev, prefs: next }));
      await putItem(next);
      return next;
    },
    [data.prefs, putItem]
  );

  const generateInsights = useCallback(async () => {
    const res = await api.generateInsights();
    if (res?.digest) {
      setData((prev) => {
        const filtered = prev.insights.filter((i) => i.weekStartDate !== res.digest.weekStartDate);
        return { ...prev, insights: [res.digest, ...filtered] };
      });
    }
    return res?.digest ?? null;
  }, []);

  const chat = useCallback(async (message) => {
    const res = await api.chat({ message });
    if (res?.prefsUpdate) {
      setData((prev) => ({
        ...prev,
        prefs: { ...(prev.prefs || {}), ...res.prefsUpdate, sk: 'prefs#main' }
      }));
    }
    return res;
  }, []);

  return {
    data,
    loading,
    online,
    error,
    refresh,
    saveGoal,
    deleteGoal,
    saveHabit,
    deleteHabit,
    checkInHabit,
    toggleHabitPause,
    saveMood,
    savePrefs,
    generateInsights,
    chat
  };
}
